import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import {
  CurrencyInputProps,
  DateInputProps,
  FilterInputProps,
  NumberInputProps,
  SelectInputProps,
  TextInputProps,
  WintFormConnect,
} from "models/propTypes/wintFormPropsTypes";
import { DocumentDraftCreateFormReceiptSectionProps } from "models/propTypes/documentDraftPropTypes";
import { DimensionEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { CommonConstants, IconDefinitionConstants } from "utils/constants";
import { OTHER_PAYMENT_METHOD_ID } from "utils/constants/receiptConstants";
import { ReceiptFormTypes } from "models/formTypes";
//#endregion
//#region components
import {
  GroupSection,
  VerticalFormField,
  DateInput,
  CurrencyInput,
  CurrencySelectionInput,
  SelectInput,
  TextAreaInput,
  TextInput,
  Toggle,
  NumberInput,
  DimensionChipSelector,
  FormFieldLayout,
  FilterInput,
  EditableTable,
  ExpandableSectionSummary,
  Description,
} from "components/common/wintForms";
import { useDimensionMaps, StaticInfoMessage} from "components/common";
import { HighReceiptAmountBanner, HighReceiptAmountFloatingMessage } from "components/receipt";
import OptionalExpandableSection from "components/common/wintForms/formSections/optionalExpandableSection";
import { CostForwardingFields } from "components/costForwardingMarking";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, ReceiptHelper } from "utils/helpers";
import { OwnExpensePayoutInfoVariant } from "utils/helpers/receiptHelper";
import { ReceiptService } from "services";
import { useReceiptPaymentMethodService, useUserService } from "serviceHooks";
import { Grid } from "@mui/material";

const ownExpensePayoutInfoMessages: Record<OwnExpensePayoutInfoVariant, string> = {
  automaticPayout: TranslationKeys.Receipt.OwnExpensePayoutInfo,
  pendingPayout: TranslationKeys.Receipt.OwnExpensePayoutInfoPending,
};

const ReceiptSection: React.FC<DocumentDraftCreateFormReceiptSectionProps> = (props) => {
  const {
    connect,
    values,
    receiptCategories,
    topReceiptPaymentMethods,
    receiptPaymentMethods,
    receiptPaymentMethodActions,
    setIsReceiptLoading,
  } = props;

  const receiptPaymentMethodService = useReceiptPaymentMethodService();
  const userService = useUserService();
  const [paymentMethod, setPaymentMethod] = React.useState<WintSuperkollAPIModel.ReceiptPaymentMethod | null>(null);
  const noBankIntegration = useSelector((store: AppState) => store.auth.selectedCompany.noBankIntegration);
  const autoReceiptPayments = useSelector((store: AppState) => store.auth.selectedCompany.autoReceiptPayments);
  const hasReceiptApprover = useSelector((store: AppState) => store.auth.selectedCompany.hasReceiptApprover);
  const [submittedReceiptCount, setSubmittedReceiptCount] = React.useState<number>();
  const [ownExpensePayoutInfoDismissed, setOwnExpensePayoutInfoDismissed] = React.useState(false);

  const paymentMethodId = values.receipt.paymentMethod;

  const categoryType: WintSuperkollAPIModel.ReceiptCategoryType | undefined = values.receipt.categoryType;
  const representation = ReceiptHelper.isRepresentation(categoryType);
  const other = categoryType === WintSuperkollAPIModel.ReceiptCategoryType.Other;
  const ownExpense = paymentMethod?.type === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense;

  const otherPaymentMethod = paymentMethodId === OTHER_PAYMENT_METHOD_ID;

  const ownExpensePayoutInfoVariant = ReceiptHelper.getOwnExpensePayoutInfoVariant({
    isClassifiedReceipt: !!props.isClassifiedReceipt,
    isOwnExpense: ownExpense,
    autoReceiptPayments,
    hasReceiptApprover,
    isManualPayment: !!values.receipt.isManualPayment,
    submittedReceiptCount,
    dismissed: ownExpensePayoutInfoDismissed,
  });

  const categorySetValue = (value: number | string | undefined, shouldValidate?: boolean) => {
    const categoryConnector = connect("receipt.category");
    const categoryTypeConnector = connect("receipt.categoryType");
    categoryConnector.setValue(value, shouldValidate);

    const categoryItem = receiptCategories.find((c) => c.value === value);
    if (categoryItem === undefined) {
      categoryTypeConnector.setValue(undefined, false);
    } else {
      categoryTypeConnector.setValue(categoryItem.data.type, false);
    }
  };

  const { currentUser, selectedCompany } = useSelector(
    (store: AppState) => ({
      currentUser: store.auth.loggedInUser.name,
      selectedCompany: store.auth.selectedCompany,
    }),
    (left, right) => left.currentUser !== right.currentUser && left.selectedCompany.id !== right.selectedCompany.id
  );

  const { forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);
  const isOptional = forcedDimensionMap.length === 0;

  React.useEffect(() => {
    if (selectedCompany.receiptCreator) {
      const canBePaidOutConnector = connect("receipt.canBePayedOut");
      ReceiptService.getReceiptsCanBePayedOut()
        .then((res) => {
          canBePaidOutConnector.setValue(res.data);
        })
        .finally(() => {
          setIsReceiptLoading(false);
        });
    } else {
      setIsReceiptLoading(false);
    }
  }, [currentUser]);

  React.useEffect(() => {
    const paymentMethod = receiptPaymentMethods.length === 1 ? receiptPaymentMethods[0] : undefined;

    if (!paymentMethod) {
      return;
    }

    const paymentMethodConnector = connect("receipt.paymentMethod");
    paymentMethodConnector.setValue(Number(paymentMethod.id));
  }, [receiptPaymentMethods]);

  React.useEffect(() => {
    if (props.isClassifiedReceipt) {
      return;
    }

    userService
      .getUploadedReceiptCount()
      .then(setSubmittedReceiptCount)
      .catch(() => setSubmittedReceiptCount(undefined));
  }, []);

  React.useEffect(() => {
    if (paymentMethodId && paymentMethodId !== OTHER_PAYMENT_METHOD_ID) {
      receiptPaymentMethodService.get(paymentMethodId).then((result) => {
        setPaymentMethod(result.data);
      });
    } else {
      setPaymentMethod(null);
    }
  }, [paymentMethodId]);

  return (
    <>
      <OptionalExpandableSection
        isActive={props.isClassifiedReceipt ?? false}
        summary={
          <ExpandableSectionSummary
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Information)}
          />
        }
        initiallyOpen={true}
      >
        <GroupSection header="" className={cssExports.noTitle}>
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...connect("receipt.date")}
            label={TranslationKeys.Receipt.PurchaseDate}
          />
          <VerticalFormField<SelectInputProps>
            Component={SelectInput}
            {...connect("receipt.category")}
            setValue={categorySetValue}
            label={TranslationKeys.Common.RefersTo}
            icon={IconDefinitionConstants.WVat}
            items={receiptCategories}
          />
          {representation ? (
            <>
            <VerticalFormField<TextInputProps>
                Component={TextAreaInput}
                {...connect("receipt.purpose")}
                label={TranslationKeys.Receipt.ReceiptRepresentationFieldText}
                icon={IconDefinitionConstants.WCommentDots}
                minRows={2}
              />
            <Grid container direction="column" spacing={1} mt={1}>
              <Grid item>
                <Description horizontal>
                  {LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Receipt.ReceiptRepresentationDescriptionHelpText
                  )}
                </Description>
              </Grid>
            </Grid>
              <EditableTable<ReceiptFormTypes.ReceiptPerson>
                connect={connect as WintFormConnect<any>}
                name={"receipt.persons"}
                value={values.receipt.persons ?? []}
                fields={{
                  name: {
                    Component: TextInput,
                    extraProps: {
                      placeholder: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Name),
                    },
                    widthColumnCount: 5,
                  },
                  company: {
                    Component: TextInput,
                    extraProps: {
                      placeholder: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Company),
                    },
                  },
                }}
                idField="id"
                defaultRow={{ name: undefined, company: undefined }}
                isCompactTable
              />
            </>
          ) : (
            <></>
          )}
          {other ? (
            <>
              <VerticalFormField<TextInputProps>
                Component={TextInput}
                {...connect("receipt.otherExplanation")}
                label={TranslationKeys.Receipt.ReceiptOtherExplanationHelptext}
                maxLength={300}
              ></VerticalFormField>
            </>
          ) : (
            <></>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} md={7}>
              <VerticalFormField<CurrencyInputProps>
                Component={CurrencyInput}
                {...connect("receipt.amount")}
                label={TranslationKeys.Common.AmountInclVAT}
                icon={IconDefinitionConstants.WCosts2}
                currency={values.receipt.currency}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <VerticalFormField
                Component={CurrencySelectionInput}
                {...connect("receipt.currency")}
                label={TranslationKeys.Common.Currency}
                icon={IconDefinitionConstants.WMoney}
              />
            </Grid>
          </Grid>
          {props.isClassifiedReceipt ? (
            <HighReceiptAmountFloatingMessage
              amount={values.receipt.amount}
              currency={values.receipt.currency}
              categoryType={values.receipt.categoryType}
              receiptCategoryId={values.receipt.category}
              date={values.receipt.date}
            />
          ) : (
            <Grid container mt={1}>
              <Grid item xs={12}>
                <HighReceiptAmountBanner
                  amount={values.receipt.amount}
                  currency={values.receipt.currency}
                  categoryType={values.receipt.categoryType}
                  receiptCategoryId={values.receipt.category}
                  date={values.receipt.date}
                />
              </Grid>
            </Grid>
          )}
          <VerticalFormField<FilterInputProps>
            Component={FilterInput}
            {...connect("receipt.paymentMethod")}
            label={TranslationKeys.Receipt.PaymentMethod}
            icon={IconDefinitionConstants.WIncome2}
            items={topReceiptPaymentMethods.concat(receiptPaymentMethods)}
            actions={receiptPaymentMethodActions}
          />
          {ownExpensePayoutInfoVariant && (
            <FormFieldLayout direction="vertical" hideMainLabel>
              <StaticInfoMessage fullWidth onClose={() => setOwnExpensePayoutInfoDismissed(true)}>
                {LocalizationHelperInstance.getFormattedMessage(
                  ownExpensePayoutInfoMessages[ownExpensePayoutInfoVariant]
                )}
              </StaticInfoMessage>
            </FormFieldLayout>
          )}
          {otherPaymentMethod && (
            <VerticalFormField<TextInputProps>
              Component={TextInput}
              {...connect("receipt.otherPaymentMethodExplanation")}
              icon={IconDefinitionConstants.WCreditCard}
              label={TranslationKeys.Receipt.OtherPaymentExplanation}
              maxLength={300}
            />
          )}
          {(
            <VerticalFormField<TextInputProps>
              Component={TextAreaInput}
              {...connect("receipt.internalNote")}
              label={TranslationKeys.Receipt.InternalNote}
              descriptionText={TranslationKeys.Receipt.InternalNoteDescriptionText}
              icon={IconDefinitionConstants.WCommentDots}
              minRows={2}
            />
          )}
          {props.showCostForwarding && <CostForwardingFields connect={connect} values={values} />}
          {props.isClassifiedReceipt && ownExpense && !noBankIntegration && (
            <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptOwnExpenseMessage)}
            </StaticInfoMessage>
          )}
        </GroupSection>
      </OptionalExpandableSection>
      {values.receipt.hasDimensions ? (
        <OptionalExpandableSection
          isActive={props.isClassifiedReceipt ?? false}
          initiallyOpen={false}
          summary={
            <ExpandableSectionSummary
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
              subText={
                isOptional
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional)
                  : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Compulsory)
              }
            />
          }
        >
          <GroupSection
            header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
            description={TranslationKeys.Document.TagsHelpText}
            className={cssExports.noBorder}
          >
            <DimensionChipSelector
              {...connect("receipt.dimensions")}
              type={DimensionEnums.DimensionCategory.Receipt}
              allowDefaultDimensions
              amount={values.receipt.amount ?? 0}
              currency={values.receipt.currency}
              layout={<FormFieldLayout direction="vertical" />}
            />
          </GroupSection>
        </OptionalExpandableSection>
      ) : (
        <></>
      )}
      {!values.receipt.canBePayedOut && !props.isClassifiedReceipt ? (
        <GroupSection
          header={TranslationKeys.Common.ReceiptPaymentInformation}
          description={TranslationKeys.Receipt.PaymentInfoDescription}
        >
          <VerticalFormField
            Component={Toggle}
            {...connect("receipt.isManualPayment")}
            label={TranslationKeys.Payments.ManualPayment}
            descriptionText={TranslationKeys.Receipt.ManualPaymentDescription}
            showMainLabel
          />
          <VerticalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("receipt.personNumber")}
            label={TranslationKeys.Settings.PersonNumber}
            allowLeadingZeros
            format={CommonConstants.PersonNumberMask}
          />
          <VerticalFormField<TextInputProps>
            Component={TextInput}
            {...connect("receipt.clearingNumber")}
            label={TranslationKeys.Settings.ClearingNumber}
            descriptionText={TranslationKeys.Settings.ClearingNumberDescription}
          />
          <VerticalFormField<TextInputProps>
            Component={TextInput}
            {...connect("receipt.bankAccount")}
            label={TranslationKeys.Settings.AccountNumber}
            descriptionText={TranslationKeys.Settings.AccountNumberDescription}
          />
        </GroupSection>
      ) : (
        <></>
      )}
    </>
  );
};

export default ReceiptSection;
